// src/App.tsx
import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, NavLink } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import api from './services/api';
import { type Event } from './types';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EventsPage from './pages/EventsPage';
import CreateEventPage from './pages/CreateEventPage';
import EventDetailPage from './pages/EventDetailPage';
import AccountPage from './pages/AccountPage';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import './App.css';
import TeamsPage from './pages/TeamsPage';
import CreateTeamPage from './pages/CreateTeamPage';
import EditTeamPage from './pages/EditTeamPage';
import AnalyticsTracker from './components/AnalyticsTracker';
import TeamDetailPage from './pages/TeamDetailPage';
import EditEventPage from './pages/EditEventPage';
import ContactPage from './pages/ContactPage';


// --- Komponent Strony Głównej dla Zalogowanego Użytkownika ---
const HomePage: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [dashboardData, setDashboardData] = useState<{ favoriteEvents: Event[], upcomingEvents: Event[] } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/Events/dashboard')
            .then(response => setDashboardData(response.data))
            .catch(err => console.error("Błąd pobierania danych na stronę główną:", err))
            .finally(() => setLoading(false));
    }, []);

    // Helpers do grupowania po dyscyplinie
    const norm = (s?: string) => (s || '').toString().trim().toLowerCase();
    const sportLabel = (discipline?: string) => {
        const key = norm(discipline);
        if (!key) return t('common.unknown') || 'Unknown';
        const i18nKey = `sportTypes.${key}` as const;
        const translated = t(i18nKey);
        if (!translated || translated === i18nKey) {
            const raw = discipline || '';
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        }
        return translated;
    };

    const groupByDiscipline = (list: Event[]) => {
        const map = new Map<string, Event[]>();
        for (const e of list) {
            const key = norm((e as any).sportType ?? (e as any).discipline ?? '');
            const bucket = map.get(key) || [];
            bucket.push(e);
            map.set(key, bucket);
        }
        return map;
    };

    const favGrouped = useMemo(() => groupByDiscipline(dashboardData?.favoriteEvents ?? []), [dashboardData]);
    const upcGrouped = useMemo(() => groupByDiscipline(dashboardData?.upcomingEvents ?? []), [dashboardData]);

    const favKeys = useMemo(() => Array.from(favGrouped.keys()).sort((a, b) => sportLabel(a).localeCompare(sportLabel(b))), [favGrouped]);
    const upcKeys = useMemo(() => Array.from(upcGrouped.keys()).sort((a, b) => sportLabel(a).localeCompare(sportLabel(b))), [upcGrouped]);

    if (loading) return <p className="text-center text-white">Ładowanie danych...</p>;

    return (
        <div className="space-y-12">
            <div>
                <h1 className="text-3xl font-bold text-white mb-4">{t('home.welcome')}, {user?.unique_name}!</h1>
            </div>

            {/* Sekcja Ulubionych (z podziałem na dyscypliny) */}
            <div>
                <h2 className="text-2xl font-semibold text-white mb-4">⭐ {t('home.favorites')}</h2>
                {(dashboardData?.favoriteEvents?.length ?? 0) > 0 ? (
                    <div className="space-y-8">
                        {favKeys.map(key => {
                            const list = favGrouped.get(key) || [];
                            if (list.length === 0) return null;
                            return (
                                <section key={`fav-${key}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-xl font-bold text-white">
                                            {sportLabel(key)}
                                            <span className="ml-3 text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-300 align-middle">
                                                {list.length}
                                            </span>
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {list.map(event => (
                                            <Link to={`/event/${event.id}`} key={event.id} className="block bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors">
                                                <h4 className="font-bold text-lg text-white truncate">{event.eventName}</h4>
                                                <p className="text-sm text-gray-400 truncate">{event.teamAName} vs {event.teamBName}</p>
                                                {['InProgress', 'Finished'].includes(event.status as any) && (
                                                    <div className="mt-2 text-yellow-300 text-sm">
                                                        {Number((event as any).scoreA ?? 0)} : {Number((event as any).scoreB ?? 0)}
                                                    </div>
                                                )}
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-gray-800 rounded-lg p-6">
                        <p className="text-gray-400">{t('home.noFavorites')}</p>
                    </div>
                )}
            </div>

            {/* Sekcja Najbliższych (z podziałem na dyscypliny) */}
            <div>
                <h2 className="text-2xl font-semibold text-white mb-4">📅 {t('home.soon')}</h2>
                {(dashboardData?.upcomingEvents?.length ?? 0) > 0 ? (
                    <div className="space-y-8">
                        {upcKeys.map(key => {
                            const list = upcGrouped.get(key) || [];
                            if (list.length === 0) return null;
                            return (
                                <section key={`upc-${key}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-xl font-bold text-white">
                                            {sportLabel(key)}
                                            <span className="ml-3 text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-300 align-middle">
                                                {list.length}
                                            </span>
                                        </h3>
                                    </div>
                                    <div className="space-y-4">
                                        {list.map(event => (
                                            <Link to={`/event/${event.id}`} key={event.id} className="bg-gray-800 p-4 rounded-lg flex justify-between items-center hover:bg-gray-700 transition-colors">
                                                <div className="min-w-0">
                                                    <h4 className="font-bold text-lg text-white truncate">{event.eventName}</h4>
                                                    <p className="text-sm text-gray-400 truncate">{event.teamAName} vs {event.teamBName}</p>
                                                </div>
                                                <div className="text-right text-sm text-gray-300 flex-shrink-0">
                                                    <p>{new Date(event.startTime).toLocaleDateString('pl-PL')}</p>
                                                    <p>{new Date(event.startTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-gray-800 rounded-lg p-6">
                        <p className="text-gray-400">{t('home.onUpcoming')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Strona powitalna (publiczne events) ---
const WelcomePage: React.FC = () => {
    const { t } = useTranslation();
    const [publicEvents, setPublicEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPublicEvents = () => {
            api.get('/Events/public')
                .then(response => setPublicEvents(response.data))
                .catch(err => console.error("Błąd pobierania wydarzeń publicznych:", err))
                .finally(() => setLoading(false));
        };

        fetchPublicEvents();
        const intervalId = setInterval(fetchPublicEvents, 5000);
        return () => clearInterval(intervalId);
    }, []);

    // Grupujemy wydarzenia po dyscyplinie
    const eventsByDiscipline = publicEvents.reduce((acc, event) => {
        const discipline = (event as any).sportType ?? (event as any).discipline ?? 'unknown';
        acc[discipline] = acc[discipline] || [];
        acc[discipline].push(event);
        return acc;
    }, {} as { [key: string]: Event[] });

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h1 className="text-5xl font-extrabold text-white">The Scoreboard</h1>
                <p className="mt-4 text-xl text-gray-300">{t('home.description')}</p>
            </div>

            <hr className="border-gray-700" />

            {loading ? (
                <p className="text-center text-gray-400">Ładowanie wydarzeń...</p>
            ) : Object.keys(eventsByDiscipline).length === 0 ? (
                <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
                    <p>{t('home.noPublicEvents')}</p>
                </div>
            ) : (
                Object.keys(eventsByDiscipline).map(discipline => (
                    <div key={discipline}>
                        <h2 className="text-2xl font-bold text-white mb-4">{t(`sportTypes.${discipline}` as const)}</h2>
                        <div className="space-y-4">
                            {eventsByDiscipline[discipline].map(event => (
                                <Link to={`/event/${event.id}`} key={event.id} className="block bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors shadow-md">
                                    <div className="text-center mb-3">
                                        <h3 className="text-xl font-bold text-white">{event.eventName}</h3>
                                        <p className="text-xs text-gray-500">
                                            <span>{t('eventDetail.status')}: <span className="font-semibold text-yellow-400">{t(`statuses.${event.status}` as const)}</span></span>
                                            <span className="mx-2">|</span>
                                            <span>{t('eventDetail.startTime')}: {new Date(event.startTime).toLocaleString('pl-PL')}</span>
                                        </p>
                                    </div>
                                    <div className="bg-gray-900 rounded-md p-3 flex justify-between items-center">
                                        <span className="w-2/5 text-right text-lg font-semibold text-white truncate">{event.teamAName}</span>
                                        <span className="text-2xl font-black text-yellow-400 mx-4">{Number((event as any).scoreA ?? 0)} - {Number((event as any).scoreB ?? 0)}</span>
                                        <span className="w-2/5 text-left text-lg font-semibold text-white truncate">{event.teamBName}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

const ProtectedRoute = ({ children }: { children: React.JSX.Element }) => {
    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

// --- Główny Komponent Aplikacji ---
function App() {
    const { isAuthenticated, logout } = useAuth();
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navLinkClasses = "text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors";
    const activeNavLinkClasses = "bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium";
    const mobileNavLinkClasses = "block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700";

    return (
        <Router>
            <AnalyticsTracker />
            {/* FLEX container to pin footer to bottom */}
            <div className="min-h-screen bg-gray-900 flex flex-col">
                <nav className="bg-gray-800 shadow-lg">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            {/* Lewa strona: Logo i linki na desktopie */}
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <Link to="/">
                                        <img className="h-10 w-auto" src="/logo.png" alt="The Scoreboard Logo" />
                                    </Link>
                                </div>
                                <div className="hidden md:block">
                                    <div className="ml-10 flex items-baseline space-x-4">
                                        <NavLink to="/" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.start')}</NavLink>
                                        {isAuthenticated && <NavLink to="/events" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.myEvents')}</NavLink>}
                                        {isAuthenticated && <NavLink to="/teams" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.teams')}</NavLink>}
                                        {isAuthenticated && <NavLink to="/account" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.account')}</NavLink>}
                                    </div>
                                </div>
                            </div>

                            {/* Prawa strona: Kontrolki */}
                            <div className="flex items-center">
                                {/* Kontrolki na desktopie */}
                                <div className="hidden md:flex items-center">
                                    {!isAuthenticated && (
                                        <>
                                            <NavLink to="/login" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.login')}</NavLink>
                                            <NavLink to="/register" className={({ isActive }) => isActive ? activeNavLinkClasses : navLinkClasses}>{t('nav.register')}</NavLink>
                                        </>
                                    )}
                                    <LanguageSwitcher />
                                    {isAuthenticated && (
                                        <button onClick={logout} className="ml-4 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md text-sm transition-colors">
                                            {t('home.logout')}
                                        </button>
                                    )}
                                </div>

                                {/* Kontrolki na mobile */}
                                <div className="flex items-center md:hidden">
                                    {!isAuthenticated && (
                                        <Link to="/login" title={t('nav.login')} className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                                        </Link>
                                    )}
                                    {isAuthenticated && (
                                        <button onClick={logout} title={t('home.logout')} className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                        </button>
                                    )}
                                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} type="button" className="ml-2 bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none" aria-controls="mobile-menu" aria-expanded="false">
                                        <span className="sr-only">Open main menu</span>
                                        {isMobileMenuOpen ? (
                                            <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        ) : (
                                            <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rozwijane menu mobilne */}
                    {isMobileMenuOpen && (
                        <div className="md:hidden" id="mobile-menu">
                            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                                <NavLink to="/" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.start')}</NavLink>
                                {isAuthenticated && <NavLink to="/events" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.myEvents')}</NavLink>}
                                {isAuthenticated && <NavLink to="/teams" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.teams')}</NavLink>}
                                {isAuthenticated && <NavLink to="/account" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.account')}</NavLink>}
                                {!isAuthenticated && <NavLink to="/login" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.login')}</NavLink>}
                                {!isAuthenticated && <NavLink to="/register" className={mobileNavLinkClasses} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.register')}</NavLink>}
                            </div>
                            <div className="pt-4 pb-3 border-t border-gray-700">
                                <div className="flex items-center px-5">
                                    <LanguageSwitcher />
                                </div>
                            </div>
                        </div>
                    )}
                </nav>

                {/* MAIN as flex-1 so footer sits at bottom */}
                <main className="flex-1">
                    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/register" element={<RegisterPage />} />
                                <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
                                <Route path="/create-event" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
                                <Route path="/event/:id" element={<EventDetailPage />} />
                                <Route path="/events/edit/:id" element={<ProtectedRoute><EditEventPage /></ProtectedRoute>} />
                                <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                                <Route path="/" element={isAuthenticated ? <HomePage /> : <WelcomePage />} />
                                <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
                                <Route path="/create-team" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                                <Route path="/teams/edit/:id" element={<ProtectedRoute><EditTeamPage /></ProtectedRoute>} />
                                <Route path="/teams/:id" element={<TeamDetailPage />} />
                                <Route path="/contact" element={<ContactPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                            </Routes>
                        </Suspense>
                    </div>
                </main>

                {/* FOOTER */}
                <footer className="border-t border-gray-700 text-gray-400">
                    <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-center gap-4 text-center text-sm">
                        <span>© TheScoreboard - 2025</span>
                        <span className="text-gray-600">•</span>
                        <Link to="/contact" className="underline hover:text-gray-200 transition-colors">
                            {/** używamy tłumaczeń dla spójności */}
                            {t('footer.contactLink')}
                        </Link>
                    </div>
                </footer>
            </div>
        </Router>
    );
}

export default App;
